/**
 * PrescriptionScannerDialog Component
 * 
 * Captures or uploads prescription label images and extracts
 * medication details using AI vision.
 */

import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Camera,
  Upload,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Sparkles,
  X,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import prescriptionScannerService, {
  captureImage,
  selectFromGallery,
  fileToBase64,
  extractMedicationDetails,
  validateExtractedData,
  formatForMedicationForm,
} from '@/services/prescriptionScannerService';

// Scan states
const SCAN_STATE = {
  IDLE: 'idle',
  CAPTURING: 'capturing',
  PREVIEWING: 'previewing',
  ANALYZING: 'analyzing',
  SUCCESS: 'success',
  ERROR: 'error',
};

const PrescriptionScannerDialog = ({
  isOpen,
  onClose,
  onExtracted,
}) => {
  const [scanState, setScanState] = useState(SCAN_STATE.IDLE);
  const [imageData, setImageData] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  const isNative = Capacitor.isNativePlatform();

  // Reset state
  const resetState = useCallback(() => {
    setScanState(SCAN_STATE.IDLE);
    setImageData(null);
    setExtractedData(null);
    setValidationResult(null);
    setError(null);
    setProgress(0);
  }, []);

  // Handle camera capture
  const handleCapture = async () => {
    setScanState(SCAN_STATE.CAPTURING);
    setError(null);
    
    try {
      const result = await captureImage();
      
      if (result.cancelled) {
        setScanState(SCAN_STATE.IDLE);
        return;
      }
      
      if (result.needsFileInput) {
        // Trigger file input for web
        fileInputRef.current?.click();
        setScanState(SCAN_STATE.IDLE);
        return;
      }
      
      setImageData(result);
      setScanState(SCAN_STATE.PREVIEWING);
    } catch (error) {
      console.error('Capture error:', error);
      setError('Failed to capture image. Please try again.');
      setScanState(SCAN_STATE.ERROR);
    }
  };

  // Handle gallery selection
  const handleGallery = async () => {
    setScanState(SCAN_STATE.CAPTURING);
    setError(null);
    
    try {
      const result = await selectFromGallery();
      
      if (result.cancelled) {
        setScanState(SCAN_STATE.IDLE);
        return;
      }
      
      if (result.needsFileInput) {
        fileInputRef.current?.click();
        setScanState(SCAN_STATE.IDLE);
        return;
      }
      
      setImageData(result);
      setScanState(SCAN_STATE.PREVIEWING);
    } catch (error) {
      console.error('Gallery error:', error);
      setError('Failed to select image. Please try again.');
      setScanState(SCAN_STATE.ERROR);
    }
  };

  // Handle file input (web fallback)
  const handleFileInput = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large. Please use an image under 10MB.');
      return;
    }
    
    setScanState(SCAN_STATE.CAPTURING);
    
    try {
      const result = await fileToBase64(file);
      setImageData(result);
      setScanState(SCAN_STATE.PREVIEWING);
    } catch (error) {
      console.error('File processing error:', error);
      setError('Failed to process image. Please try again.');
      setScanState(SCAN_STATE.ERROR);
    }
    
    // Reset input
    e.target.value = '';
  };

  // Analyze the image
  const handleAnalyze = async () => {
    if (!imageData?.base64) return;
    
    setScanState(SCAN_STATE.ANALYZING);
    setProgress(10);
    setError(null);
    
    try {
      // Simulate progress while waiting for AI
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 500);
      
      const result = await extractMedicationDetails(imageData.base64);
      
      clearInterval(progressInterval);
      setProgress(100);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      setExtractedData(result.data);
      setValidationResult(validateExtractedData(result.data));
      setScanState(SCAN_STATE.SUCCESS);
    } catch (error) {
      console.error('Analysis error:', error);
      setError(error.message || 'Failed to analyze image. Please try a clearer photo.');
      setScanState(SCAN_STATE.ERROR);
    }
  };

  // Use the extracted data
  const handleUseData = () => {
    if (!extractedData) return;
    
    const formattedData = formatForMedicationForm(extractedData);
    onExtracted(formattedData);
    onClose();
    resetState();
    
    toast.success('Prescription details extracted!', {
      description: 'Review and adjust the filled information as needed.',
    });
  };

  // Handle dialog close
  const handleClose = () => {
    onClose();
    resetState();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Scan Prescription Label
          </DialogTitle>
          <DialogDescription>
            Take a photo of your prescription label and we&apos;ll extract the details automatically
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Initial State - Capture Options */}
          {scanState === SCAN_STATE.IDLE && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Camera Button */}
                <button
                  onClick={handleCapture}
                  className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 hover:border-blue-400 transition-all hover:shadow-lg group"
                >
                  <div className="w-14 h-14 rounded-full bg-blue-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Camera className="h-7 w-7 text-white" />
                  </div>
                  <span className="font-medium text-blue-800">Take Photo</span>
                  <span className="text-xs text-blue-600 mt-1">
                    {isNative ? 'Use camera' : 'Upload photo'}
                  </span>
                </button>

                {/* Gallery Button */}
                <button
                  onClick={isNative ? handleGallery : () => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200 hover:border-purple-400 transition-all hover:shadow-lg group"
                >
                  <div className="w-14 h-14 rounded-full bg-purple-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <ImageIcon className="h-7 w-7 text-white" />
                  </div>
                  <span className="font-medium text-purple-800">From Gallery</span>
                  <span className="text-xs text-purple-600 mt-1">Choose existing</span>
                </button>
              </div>

              {/* Tips */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-700 mb-2">Tips for best results:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Make sure the label is well-lit and in focus</li>
                  <li>• Include the entire label in the photo</li>
                  <li>• Avoid glare or shadows on the label</li>
                  <li>• Hold the camera steady</li>
                </ul>
              </div>
            </div>
          )}

          {/* Capturing State */}
          {scanState === SCAN_STATE.CAPTURING && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
              <p className="text-slate-600">Opening camera...</p>
            </div>
          )}

          {/* Preview State */}
          {scanState === SCAN_STATE.PREVIEWING && imageData && (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden border border-slate-200">
                <img
                  src={imageData.dataUrl}
                  alt="Prescription label"
                  className="w-full h-auto max-h-64 object-contain bg-slate-100"
                />
                <button
                  onClick={resetState}
                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={resetState}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retake
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
                  onClick={handleAnalyze}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Extract Details
                </Button>
              </div>
            </div>
          )}

          {/* Analyzing State */}
          {scanState === SCAN_STATE.ANALYZING && (
            <div className="space-y-4 py-8">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center mb-4 animate-pulse">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <p className="font-medium text-slate-700 mb-2">Analyzing prescription...</p>
                <p className="text-sm text-slate-500 mb-4">AI is extracting medication details</p>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Success State */}
          {scanState === SCAN_STATE.SUCCESS && extractedData && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-800">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium">Details extracted successfully!</span>
              </div>

              {/* Validation warnings */}
              {validationResult && !validationResult.isValid && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-800 mb-2">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Some details could not be detected</span>
                  </div>
                  <ul className="text-sm text-amber-700 list-disc pl-6">
                    {validationResult.issues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Extracted data preview */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <h4 className="font-medium text-slate-700 mb-3">Extracted Information:</h4>
                
                {extractedData.name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Medication:</span>
                    <span className="font-medium text-slate-800">{extractedData.name}</span>
                  </div>
                )}
                
                {(extractedData.dosage || extractedData.strength) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Dosage:</span>
                    <span className="font-medium text-slate-800">
                      {extractedData.dosage || extractedData.strength}
                    </span>
                  </div>
                )}
                
                {(extractedData.frequency || extractedData.directions) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Directions:</span>
                    <span className="font-medium text-slate-800 text-right max-w-[200px]">
                      {extractedData.frequency || extractedData.directions}
                    </span>
                  </div>
                )}
                
                {extractedData.quantity && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Quantity:</span>
                    <span className="font-medium text-slate-800">{extractedData.quantity}</span>
                  </div>
                )}
                
                {extractedData.refills != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Refills:</span>
                    <span className="font-medium text-slate-800">{extractedData.refills}</span>
                  </div>
                )}
                
                {extractedData.prescriber && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Prescriber:</span>
                    <span className="font-medium text-slate-800">{extractedData.prescriber}</span>
                  </div>
                )}
                
                {extractedData.pharmacy && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Pharmacy:</span>
                    <span className="font-medium text-slate-800">{extractedData.pharmacy}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={resetState}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Scan Again
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleUseData}
                >
                  Use This Data
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Error State */}
          {scanState === SCAN_STATE.ERROR && (
            <div className="space-y-4">
              <div className="flex flex-col items-center py-8">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <p className="font-medium text-slate-700 mb-2">Analysis Failed</p>
                <p className="text-sm text-slate-500 text-center px-4">{error}</p>
              </div>
              
              <Button
                variant="outline"
                className="w-full"
                onClick={resetState}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}
        </div>

        {/* Hidden file input for web fallback */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileInput}
          className="hidden"
        />
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionScannerDialog;
